import React, { useCallback, useEffect, useRef, useState } from "react";
import Alert from "../../../../components/Alert";
import Toast from "../../../../components/Toast";
import { ApplicationListCard } from "../Components/ApplicationListCard";
import { sendMessageToApplication } from "../api/job_api";
import { useJobsListViewModel } from "../viewmodel/job.viewmodel";
import DefaultModal from "../../CompanySettings/JobApproval/component/dialog";
import {
  hideJOBToast,
  setApplicationCurrentPage,
  setApplicationLimit,
  setJOBToast,
} from "../viewmodel/job.action";
import { ApplicationStatus } from "../model/jobEnums";
import ApplicationDetailsView from "./ApplicationDetailsPage";
import { ApplicationGridCard } from "../Components/ApplicationGridCard";
import TablePagination from "../Components/TablePagination";
import ApplicationContactModal from "../Components/applicationContactModel";
import { JobApplication } from "../model/JobModel";

const applicationStatusLabels: Record<string, string> = {
  all: "All Statuses",
  submitted: "Submitted",
  [ApplicationStatus.IN_REVIEW]: "Under Review",
  [ApplicationStatus.ON_HOLD]: "On Hold",
  [ApplicationStatus.SHORTLISTED]: "Shortlisted",
  [ApplicationStatus.INTERVIEW_SCHEDULED]: "Interview Scheduled",
  [ApplicationStatus.INTERVIEWED]: "Interview Completed",
  [ApplicationStatus.OFFERED]: "Offer Extended",
  [ApplicationStatus.DECLINED]: "Offer Declined",
  [ApplicationStatus.HIRED]: "Hired",
  [ApplicationStatus.REJECTED]: "Rejected",
  [ApplicationStatus.WITHDRAWN]: "Withdrawn",
};

const applicationStatusOptions = [
  "all",
  "submitted",
  ...Object.values(ApplicationStatus),
].map((value) => ({
  value,
  label: applicationStatusLabels[value] || value.replace(/_/g, " "),
}));

const ApplicationManagement = ({ jobId }: { jobId?: string }) => {
  const {
    state,
    fetchJobApplications,
    dispatch,
    handleViewApplication,
    handleViewContact,
    handleChangeApplicationStatus,
  } = useJobsListViewModel();
  const applications = state.JobApplications;
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<"list" | "grid">("grid");
  const fetchJobApplicationsRef = useRef(fetchJobApplications);

  useEffect(() => {
    fetchJobApplicationsRef.current = fetchJobApplications;
  }, [fetchJobApplications]);

  const fetchApplicationsWithParams = useCallback((page = state.applicationCurrentPage) => {
    if (!jobId) return;

    const trimmedSearch = searchTerm.trim();
    const params = {
      jobId: jobId,
      sort_by: state.applicationsort_by,
      include_profile: state.applicationInclude_profile,
      limit: state.applicationLimit,
      offset: (page - 1) * state.applicationLimit,
      search: trimmedSearch || undefined,
      status: statusFilter !== "all" ? statusFilter : undefined,
    };

    fetchJobApplicationsRef.current(params);
  }, [
    jobId,
    searchTerm,
    state.applicationCurrentPage,
    state.applicationLimit,
    state.applicationsort_by,
    state.applicationInclude_profile,
    statusFilter,
  ]);

  useEffect(() => {
    const delay = setTimeout(() => {
      fetchApplicationsWithParams(state.applicationCurrentPage);
    }, 500);

    return () => clearTimeout(delay);
  }, [
    fetchApplicationsWithParams,
    state.applicationCurrentPage,
  ]);
  const handleContactModel = (application: JobApplication) => {
    dispatch({
      type: "SET_CONTACT_POPUP",
      payload: {
        open: true,
        application: application,
      },
    });
  };

  const handleSendMessage = async (message: string, subject: string) => {
    if (!state.viewApplication) return;

    try {
      const jobSeekerId = state.viewApplication.candidate.id; // ✅ IMPORTANT

      const payload = {
        subject,
        message,
        access_type: "applicant", // or "marketplace"
        application_id: state.viewApplication.id, // ✅ correct
        job_id: state.viewApplication.job?.id, // optional
      };

      const resp = await sendMessageToApplication(jobSeekerId, payload);

      console.log("Message sent response:", resp);

      dispatch(setJOBToast({
        message: `Message sent successfully to ${state.viewApplication.candidate.first_name} ${state.viewApplication.candidate.last_name}!`,
        type: "success",
        isVisible: true,
      }));

      dispatch({
        type: "SET_CONTACT_POPUP",
        payload: { open: false, application: null },
      });
    } catch (error) {
      console.error("Failed to send message:", error);

      dispatch(setJOBToast({
        message: "Failed to send message. Please try again.",
        type: "error",
        isVisible: true,
      }));
    }
  };
  if (state.loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white rounded-lg shadow p-6">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label
              htmlFor="search"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Search Applications
            </label>
            <input
              type="text"
              id="search"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                dispatch(setApplicationCurrentPage(1));
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Search by candidate name, email, or job..."
            />
          </div>

          <div>
            <label
              htmlFor="application-status"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Status
            </label>
            <select
              id="application-status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                dispatch(setApplicationCurrentPage(1));
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            >
              {applicationStatusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() =>
                fetchApplicationsWithParams(state.applicationCurrentPage)
              }
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <Alert
          type="error"
          message={error}
          onClose={() => setError(null)}
          className="mb-6"
        />
      )}

      {/* Applications List */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <h3 className="text-lg font-medium text-gray-900">
            Applications ({state.applicationTotal})
          </h3>
        </div>
        <div className="hidden sm:flex items-center space-x-2">
          <span className="text-sm text-gray-500">View:</span>
          <div className="flex rounded-lg border border-gray-300">
            <button
              onClick={() => setViewMode("list")}
              className={`px-3 py-1 text-sm font-medium rounded-l-lg ${viewMode === "list"
                ? "bg-indigo-600 text-white"
                : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 10h16M4 14h16M4 18h16"
                />
              </svg>
            </button>
            <button
              onClick={() => setViewMode("grid")}
              className={`px-3 py-1 text-sm font-medium rounded-r-lg ${viewMode === "grid"
                ? "bg-indigo-600 text-white"
                : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
      {applications.length === 0 ? (
        <div className="px-6 py-12 text-center">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            No applications found
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || statusFilter !== "all"
              ? "Try adjusting your search or filters."
              : "Applications will appear here when candidates apply to your job postings."}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {viewMode === "grid" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
              {applications.map((application) => (
                <ApplicationGridCard
                  key={application.id}
                  applications={application}
                  onContact={() => handleContactModel(application)}
                  onView={() =>
                    handleViewApplication(
                      application.id,
                      application.candidate_id || application.candidate?.id,
                    )
                  }
                />
              ))}
            </div>
          )}
          {viewMode === "list" && (
            <div className="space-y-4">
              {applications.map((application) => (
                <ApplicationListCard
                  key={application.id}
                  applications={application}
                  onContact={() => handleContactModel(application)}
                  onView={() =>
                    handleViewApplication(
                      application.id,
                      application.candidate_id || application.candidate?.id,
                    )
                  }
                />
              ))}
            </div>
          )}
          {state.applicationTotal > state.applicationLimit && (
            <TablePagination
              currentPage={state.applicationCurrentPage}
              totalCount={state.applicationTotal}
              rowsPerPage={state.applicationLimit}
              onPageChange={(page) => dispatch(setApplicationCurrentPage(page))}
              onRowsPerPageChange={(rows) => {
                dispatch(setApplicationLimit(rows));
                dispatch(setApplicationCurrentPage(1));
              }}
              isGrid={viewMode === "grid"}
            />
          )}
        </div>
      )}
      {/* Toast Notification */}
      {/* <Toast
        isVisible={toast.isVisible}
        message={toast.message}
        type={toast.type}
        onClose={hideToast}
      /> */}
      <div>
        <DefaultModal
          className="max-w-[75vw]"
          open={state.applivationView}
          onClose={() =>
            dispatch({ type: "OPEN_APPLICATION_VIEW", payload: false })
          }
        >
          <ApplicationDetailsView
            refreshApplications={fetchApplicationsWithParams}
            state={state}
            dispatch={dispatch}
            handleViewContact={handleViewContact}
            handleChangeApplicationStatus={handleChangeApplicationStatus}
          />
        </DefaultModal>
      </div>

      <Toast
        message={state.toast.message}
        type={state.toast.type}
        isVisible={state.toast.isVisible}
        onClose={() => dispatch(hideJOBToast())}
      />
      <>
        <ApplicationContactModal
          isOpen={state.contactPopupOpen}
          onClose={() =>
            dispatch({
              type: "SET_CONTACT_POPUP",
              payload: { open: false, application: null },
            })
          }
          application={state.viewApplication}
          onSend={handleSendMessage}
        ></ApplicationContactModal>
      </>
    </div>
  );
};

export default ApplicationManagement;

//http://18.188.73.145:8000/api/v1/candidates/5af6f525-1802-4e8d-8d57-3a5c47944488/message'
//http://18.188.73.145:8000/api/v1/api/v1/candidates/5af6f525-1802-4e8d-8d57-3a5c47944488/message
